import { useState } from "react";
import type { Quote, LineItem, AISuggestion } from "@/types/quote";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Plus,
	Trash2,
	Sparkles,
	ChevronDown,
	ChevronUp,
	X,
	Lightbulb,
	DollarSign,
	FileText,
	ArrowLeft,
	Copy,
	Download,
	PenLine,
} from "lucide-react";
import { SignatureModal } from "@/components/SignatureModal";
import { QuotePreview } from "@/components/QuotePreview";
import { AISuggestionPanel } from "@/components/AISuggestionPanel";

interface QuoteEditorProps {
	quote: Quote;
	totals: { subtotal: number; totalDiscount: number; totalTax: number; total: number };
	aiSuggestions: AISuggestion[];
	loadingAi: boolean;
	onUpdateQuote: (updates: Partial<Quote>) => void;
	onUpdateClient: (updates: Partial<Quote["client"]>) => void;
	onUpdateCompany: (updates: Partial<Quote["company"]>) => void;
	onAddLineItem: () => void;
	onUpdateLineItem: (id: string, updates: Partial<LineItem>) => void;
	onRemoveLineItem: (id: string) => void;
	onAddTerm: (term: string) => void;
	onRemoveTerm: (index: number) => void;
	onSetSignature: (sig: Quote["signature"]) => void;
	onFetchPricingSuggestions: (desc: string, price: number) => void;
	onFetchTermsSuggestions: () => void;
	onFetchDescriptionSuggestion: (desc: string) => void;
	onDismissSuggestion: (index: number) => void;
	onClearSuggestions: () => void;
	onBack: () => void;
	onDuplicate: () => void;
	onExportPdf: () => void;
}

function formatCurrency(amount: number, currency: string) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

export function QuoteEditor({
	quote,
	totals,
	aiSuggestions,
	loadingAi,
	onUpdateQuote,
	onUpdateClient,
	onUpdateCompany,
	onAddLineItem,
	onUpdateLineItem,
	onRemoveLineItem,
	onAddTerm,
	onRemoveTerm,
	onSetSignature,
	onFetchPricingSuggestions,
	onFetchTermsSuggestions,
	onFetchDescriptionSuggestion,
	onDismissSuggestion,
	onClearSuggestions,
	onBack,
	onDuplicate,
	onExportPdf,
}: QuoteEditorProps) {
	const [showSignatureModal, setShowSignatureModal] = useState(false);
	const [activeTab, setActiveTab] = useState("edit");
	const [newTerm, setNewTerm] = useState("");
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

	const toggleItemExpand = (id: string) => {
		setExpandedItems((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	return (
		<div className="min-h-screen">
			{/* Header */}
			<header className="sticky top-0 z-40 bg-[hsl(220,16%,7%)]/80 backdrop-blur-xl border-b border-zinc-700/50">
				<div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<button
							onClick={onBack}
							className="text-zinc-500 hover:text-zinc-300 transition-colors"
						>
							<ArrowLeft className="w-5 h-5" />
						</button>
						<Separator orientation="vertical" className="h-6" />
						<div>
							<input
								type="text"
								value={quote.title}
								onChange={(e) => onUpdateQuote({ title: e.target.value })}
								className="text-lg font-medium text-zinc-100 bg-transparent border-none outline-none focus:ring-0 p-0"
								placeholder="Proposal title"
							/>
							<div className="flex items-center gap-2 mt-0.5">
								<span className="text-xs text-zinc-500">{quote.quoteNumber}</span>
								<Badge
									variant="secondary"
									className="text-[10px] px-1.5 py-0 font-normal capitalize"
								>
									{quote.status}
								</Badge>
							</div>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="sm" onClick={onDuplicate} className="text-zinc-400 hover:text-zinc-200">
							<Copy className="w-4 h-4 mr-1.5" />
							Duplicate
						</Button>
						<Button variant="ghost" size="sm" onClick={() => setShowSignatureModal(true)} className="text-zinc-400 hover:text-zinc-200">
							<PenLine className="w-4 h-4 mr-1.5" />
							Sign
						</Button>
						<Button size="sm" onClick={onExportPdf} className="bg-blue-600 hover:bg-blue-500 text-white">
							<Download className="w-4 h-4 mr-1.5" />
							Export PDF
						</Button>
					</div>
				</div>
			</header>

			<div className="max-w-7xl mx-auto px-6 py-8">
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList className="bg-zinc-800 mb-8">
						<TabsTrigger value="edit">Editor</TabsTrigger>
						<TabsTrigger value="preview">Preview</TabsTrigger>
					</TabsList>

					<TabsContent value="edit">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
							{/* Main editor */}
							<div className="lg:col-span-2 space-y-8">
								{/* Company & Client Info */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
										<CardContent className="p-6">
											<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-4">
												From
											</h3>
											<div className="space-y-3">
												<Input
													placeholder="Company name"
													value={quote.company.name}
													onChange={(e) => onUpdateCompany({ name: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Input
													placeholder="Email"
													value={quote.company.email}
													onChange={(e) => onUpdateCompany({ email: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Input
													placeholder="Phone"
													value={quote.company.phone}
													onChange={(e) => onUpdateCompany({ phone: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Textarea
													placeholder="Address"
													value={quote.company.address}
													onChange={(e) => onUpdateCompany({ address: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 text-sm resize-none placeholder:text-zinc-600"
													rows={2}
												/>
											</div>
										</CardContent>
									</Card>

									<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
										<CardContent className="p-6">
											<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-4">
												To
											</h3>
											<div className="space-y-3">
												<Input
													placeholder="Client name"
													value={quote.client.name}
													onChange={(e) => onUpdateClient({ name: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Input
													placeholder="Company"
													value={quote.client.company}
													onChange={(e) => onUpdateClient({ company: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Input
													placeholder="Email"
													value={quote.client.email}
													onChange={(e) => onUpdateClient({ email: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
												<Input
													placeholder="Phone"
													value={quote.client.phone}
													onChange={(e) => onUpdateClient({ phone: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
												/>
											</div>
										</CardContent>
									</Card>
								</div>

								{/* Dates */}
								<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
									<CardContent className="p-6">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<label className="text-xs font-medium uppercase tracking-wider text-zinc-500 block mb-2">
													Issue Date
												</label>
												<Input
													type="date"
													value={quote.date}
													onChange={(e) => onUpdateQuote({ date: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm"
												/>
											</div>
											<div>
												<label className="text-xs font-medium uppercase tracking-wider text-zinc-500 block mb-2">
													Valid Until
												</label>
												<Input
													type="date"
													value={quote.validUntil}
													onChange={(e) => onUpdateQuote({ validUntil: e.target.value })}
													className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm"
												/>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Line Items */}
								<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
									<CardContent className="p-6">
										<div className="flex items-center justify-between mb-5">
											<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500">
												Line Items
											</h3>
											<Button
												variant="ghost"
												size="sm"
												onClick={onAddLineItem}
												className="text-zinc-400 hover:text-zinc-200 h-8"
											>
												<Plus className="w-3.5 h-3.5 mr-1" />
												Add Item
											</Button>
										</div>

										{quote.items.length === 0 ? (
											<div className="text-center py-12 text-zinc-500">
												<FileText className="w-8 h-8 mx-auto mb-3 opacity-50" />
												<p className="text-sm">No items yet. Add your first line item.</p>
											</div>
										) : (
											<div className="space-y-2">
												{/* Header */}
												<div className="grid grid-cols-12 gap-3 px-3 pb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
													<div className="col-span-5">Description</div>
													<div className="col-span-2 text-right">Qty</div>
													<div className="col-span-2 text-right">Unit Price</div>
													<div className="col-span-2 text-right">Total</div>
													<div className="col-span-1" />
												</div>

												{quote.items.map((item) => (
													<LineItemRow
														key={item.id}
														item={item}
														currency={quote.currency}
														expanded={expandedItems.has(item.id)}
														onToggle={() => toggleItemExpand(item.id)}
														onUpdate={(updates) => onUpdateLineItem(item.id, updates)}
														onRemove={() => onRemoveLineItem(item.id)}
														onAiSuggest={() =>
															onFetchPricingSuggestions(item.description, item.unitPrice)
														}
														onAiEnhance={() =>
															onFetchDescriptionSuggestion(item.description)
														}
													/>
												))}
											</div>
										)}

										{/* Totals */}
										<div className="mt-6 pt-4 border-t border-zinc-700/50">
											<div className="flex flex-col items-end space-y-1.5 text-sm">
												<div className="flex items-center gap-8">
													<span className="text-zinc-500 w-24 text-right">Subtotal</span>
													<span className="text-zinc-300 w-28 text-right font-mono">
														{formatCurrency(totals.subtotal, quote.currency)}
													</span>
												</div>
												{totals.totalDiscount > 0 && (
													<div className="flex items-center gap-8">
														<span className="text-zinc-500 w-24 text-right">Discount</span>
														<span className="text-emerald-400 w-28 text-right font-mono">
															-{formatCurrency(totals.totalDiscount, quote.currency)}
														</span>
													</div>
												)}
												{totals.totalTax > 0 && (
													<div className="flex items-center gap-8">
														<span className="text-zinc-500 w-24 text-right">Tax</span>
														<span className="text-zinc-300 w-28 text-right font-mono">
															{formatCurrency(totals.totalTax, quote.currency)}
														</span>
													</div>
												)}
												<Separator className="w-52 my-1" />
												<div className="flex items-center gap-8">
													<span className="text-zinc-100 w-24 text-right font-medium">Total</span>
													<span className="text-zinc-100 w-28 text-right text-lg font-semibold font-mono">
														{formatCurrency(totals.total, quote.currency)}
													</span>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Terms */}
								<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
									<CardContent className="p-6">
										<div className="flex items-center justify-between mb-5">
											<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500">
												Terms & Conditions
											</h3>
											<Button
												variant="ghost"
												size="sm"
												onClick={onFetchTermsSuggestions}
												disabled={loadingAi}
												className="text-violet-400 h-8 hover:text-violet-300 hover:bg-violet-500/10"
											>
												<Sparkles className="w-3.5 h-3.5 mr-1" />
												Suggest Terms
											</Button>
										</div>

										<div className="space-y-2 mb-4">
											{quote.terms.map((term, i) => (
												<div key={i} className="flex items-start gap-3 group">
													<span className="text-zinc-600 text-sm mt-0.5 font-mono w-5 shrink-0">
														{i + 1}.
													</span>
													<p className="text-sm text-zinc-300 leading-relaxed flex-1">{term}</p>
													<button
														onClick={() => onRemoveTerm(i)}
														className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all mt-0.5 shrink-0"
													>
														<X className="w-3.5 h-3.5" />
													</button>
												</div>
											))}
										</div>

										<div className="flex gap-2">
											<Input
												placeholder="Add a custom term..."
												value={newTerm}
												onChange={(e) => setNewTerm(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter" && newTerm.trim()) {
														onAddTerm(newTerm.trim());
														setNewTerm("");
													}
												}}
												className="border-zinc-700 bg-zinc-800 text-zinc-200 h-9 text-sm placeholder:text-zinc-600"
											/>
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													if (newTerm.trim()) {
														onAddTerm(newTerm.trim());
														setNewTerm("");
													}
												}}
												className="shrink-0 h-9 border-zinc-700 text-zinc-300 hover:bg-zinc-700"
											>
												Add
											</Button>
										</div>
									</CardContent>
								</Card>

								{/* Notes */}
								<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
									<CardContent className="p-6">
										<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-4">
											Notes
										</h3>
										<Textarea
											placeholder="Additional notes for the client..."
											value={quote.notes}
											onChange={(e) => onUpdateQuote({ notes: e.target.value })}
											className="border-zinc-700 bg-zinc-800 text-zinc-200 text-sm resize-none placeholder:text-zinc-600"
											rows={3}
										/>
									</CardContent>
								</Card>

								{/* Signature */}
								{quote.signature && (
									<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
										<CardContent className="p-6">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500">
													Signature
												</h3>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => onSetSignature(null)}
													className="text-zinc-500 h-7 text-xs"
												>
													Remove
												</Button>
											</div>
											<div className="border border-zinc-700 rounded-lg p-4 bg-zinc-800 inline-block">
												{quote.signature.type === "draw" ? (
													<img
														src={quote.signature.data}
														alt="Signature"
														className="h-16 object-contain"
													/>
												) : (
													<span
														className="text-2xl italic text-zinc-300"
														style={{ fontFamily: "Georgia, serif" }}
													>
														{quote.signature.data}
													</span>
												)}
											</div>
											<p className="text-xs text-zinc-500 mt-2">
												{quote.signature.name} — {quote.signature.date}
											</p>
										</CardContent>
									</Card>
								)}
							</div>

							{/* Sidebar */}
							<div className="space-y-6">
								{/* AI Suggestions */}
								<AISuggestionPanel
									suggestions={aiSuggestions}
									loading={loadingAi}
									onDismiss={onDismissSuggestion}
									onClear={onClearSuggestions}
									onApplyTerm={onAddTerm}
									onApplyDescription={(desc) => {
										const firstItem = quote.items[0];
										if (firstItem) {
											onUpdateLineItem(firstItem.id, { description: desc });
										}
									}}
								/>

								{/* Quick Summary */}
								<Card className="border-zinc-700/50 shadow-none bg-zinc-800/50">
									<CardContent className="p-6">
										<h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-4">
											Summary
										</h3>
										<div className="space-y-3">
											<div className="flex justify-between text-sm">
												<span className="text-zinc-400">Items</span>
												<span className="text-zinc-100 font-medium">{quote.items.length}</span>
											</div>
											<div className="flex justify-between text-sm">
												<span className="text-zinc-400">Terms</span>
												<span className="text-zinc-100 font-medium">{quote.terms.length}</span>
											</div>
											<Separator />
											<div className="flex justify-between">
												<span className="text-sm text-zinc-400">Total</span>
												<span className="text-lg font-semibold text-zinc-100 font-mono">
													{formatCurrency(totals.total, quote.currency)}
												</span>
											</div>
										</div>

										{/* Status changer */}
										<div className="mt-5 pt-4 border-t border-zinc-700/50">
											<label className="text-xs font-medium uppercase tracking-wider text-zinc-500 block mb-2">
												Status
											</label>
											<select
												value={quote.status}
												onChange={(e) =>
													onUpdateQuote({
														status: e.target.value as Quote["status"],
													})
												}
												className="w-full h-9 rounded-md border border-zinc-700 bg-zinc-800 px-3 text-sm text-zinc-200"
											>
												<option value="draft">Draft</option>
												<option value="sent">Sent</option>
												<option value="accepted">Accepted</option>
												<option value="declined">Declined</option>
												<option value="expired">Expired</option>
											</select>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</TabsContent>

					<TabsContent value="preview">
						<QuotePreview quote={quote} totals={totals} />
					</TabsContent>
				</Tabs>
			</div>

			{showSignatureModal && (
				<SignatureModal
					onSave={(sig) => {
						onSetSignature(sig);
						setShowSignatureModal(false);
					}}
					onClose={() => setShowSignatureModal(false)}
				/>
			)}
		</div>
	);
}

function LineItemRow({
	item,
	currency,
	expanded,
	onToggle,
	onUpdate,
	onRemove,
	onAiSuggest,
	onAiEnhance,
}: {
	item: LineItem;
	currency: string;
	expanded: boolean;
	onToggle: () => void;
	onUpdate: (updates: Partial<LineItem>) => void;
	onRemove: () => void;
	onAiSuggest: () => void;
	onAiEnhance: () => void;
}) {
	const lineTotal = item.quantity * item.unitPrice;
	const discountAmt = lineTotal * (item.discount / 100);
	const afterDiscount = lineTotal - discountAmt;
	const taxAmt = afterDiscount * (item.tax / 100);
	const total = afterDiscount + taxAmt;

	return (
		<div className="group bg-zinc-800/60 border border-zinc-700/40 rounded-lg hover:border-zinc-600 transition-colors">
			<div className="grid grid-cols-12 gap-3 items-center px-3 py-2.5">
				<div className="col-span-5">
					<input
						type="text"
						value={item.description}
						onChange={(e) => onUpdate({ description: e.target.value })}
						placeholder="Item description"
						className="w-full text-sm text-zinc-200 bg-transparent outline-none placeholder:text-zinc-600"
					/>
				</div>
				<div className="col-span-2">
					<input
						type="number"
						value={item.quantity}
						onChange={(e) => onUpdate({ quantity: Number(e.target.value) || 0 })}
						min={0}
						className="w-full text-sm text-zinc-200 bg-transparent outline-none text-right font-mono"
					/>
				</div>
				<div className="col-span-2">
					<input
						type="number"
						value={item.unitPrice}
						onChange={(e) => onUpdate({ unitPrice: Number(e.target.value) || 0 })}
						min={0}
						step={0.01}
						className="w-full text-sm text-zinc-200 bg-transparent outline-none text-right font-mono"
					/>
				</div>
				<div className="col-span-2 text-right text-sm text-zinc-200 font-mono">
					{formatCurrency(total, currency)}
				</div>
				<div className="col-span-1 flex justify-end gap-0.5">
					<button
						onClick={onToggle}
						className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
					>
						{expanded ? (
							<ChevronUp className="w-3.5 h-3.5" />
						) : (
							<ChevronDown className="w-3.5 h-3.5" />
						)}
					</button>
					<button
						onClick={onRemove}
						className="p-1 text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
					>
						<Trash2 className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>

			{expanded && (
				<div className="px-3 pb-3 pt-1 border-t border-zinc-700/30">
					<div className="grid grid-cols-2 gap-3 mb-3">
						<div>
							<label className="text-[11px] text-zinc-500 mb-1 block">Discount %</label>
							<Input
								type="number"
								value={item.discount}
								onChange={(e) => onUpdate({ discount: Number(e.target.value) || 0 })}
								min={0}
								max={100}
								className="h-8 text-sm border-zinc-700 bg-zinc-800 text-zinc-200"
							/>
						</div>
						<div>
							<label className="text-[11px] text-zinc-500 mb-1 block">Tax %</label>
							<Input
								type="number"
								value={item.tax}
								onChange={(e) => onUpdate({ tax: Number(e.target.value) || 0 })}
								min={0}
								className="h-8 text-sm border-zinc-700 bg-zinc-800 text-zinc-200"
							/>
						</div>
					</div>
					<div className="flex gap-2">
						<Button
							variant="ghost"
							size="sm"
							onClick={onAiSuggest}
							className="h-7 text-xs text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
						>
							<DollarSign className="w-3 h-3 mr-1" />
							Price Check
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={onAiEnhance}
							className="h-7 text-xs text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
						>
							<Lightbulb className="w-3 h-3 mr-1" />
							Enhance Description
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
